// MyDocument.js
import React from "react";
import { Page, Text, View, Document, StyleSheet, Font } from "@react-pdf/renderer";
import WordMatching from "./WordMatching";
import FillInBlank from "./FillInBlank";
import ReadingComprehension from "./ReadingComprehension";
import SentenceUnscramble from "./SentenceUnscramble";

// Register Open Sans font
Font.register({
  family: "Open Sans",
  fonts: [
    {
      src: "https://fonts.gstatic.com/s/ptsans/v8/FUDHvzEKSJww3kCxuiAo2A.ttf",
    },
    {
      src: "https://fonts.gstatic.com/s/ptsans/v8/FUDHvzEKSJww3kCxuiAo2A.ttf",
      fontWeight: "bold",
    },
  ],
});

// Register the emoji source (if needed)
Font.registerEmojiSource({
  format: "png",
  url: "https://cdnjs.cloudflare.com/ajax/libs/twemoji/14.0.2/72x72/",
});

const styles = StyleSheet.create({
  page: {
    padding: 30,
    fontFamily: "Open Sans", // Updated to Open Sans
    fontSize: 12,
  },
  titleSection: {
    marginBottom: 20,
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: {
    fontSize: 38,
    fontWeight: "bold",
  },
  name: {
    fontSize: 16,
    fontWeight: "bold",
  },
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 8,
    textDecoration: "underline",
  },
  subsectionTitle: {
    fontSize: 14,
    fontWeight: "bold",
    marginBottom: 4,
  },
  writingPrompt: {
    fontSize: 16,
    fontWeight: "bold",
    marginBottom: 4,
  },
  writingPromptInstructions: {
    fontWeight: "bold",
  },
});

export default function MyDocument({
  typeOfContent,
  worksheetTitle,
  worksheetEmoji = [],
  wordMatchingData,
  fillInBlankData,
  readingComprehensionData,
  sentenceUnscrambleData,
  writingPrompt,
  detailedLessonPlan,
  anticipatedQuestions,
  activity,
  showWordMatching = true,
  showFillInBlank = true,
  showReadingComprehension = true,
  showWritingPrompt = true
}) {
  return (
    <Document title={worksheetTitle + "_" + typeOfContent}>
      <Page size="A4" style={styles.page}>

        <View style={styles.titleSection}>
          <Text style={styles.title}>
            {["worksheet", "homework"].includes(typeOfContent) && worksheetEmoji.length > 0
              ? worksheetEmoji[0] + " "
              : ""}
            {worksheetTitle}
          </Text>

          {["worksheet", "homework"].includes(typeOfContent) && (
            <Text style={styles.name}>Name: ________________</Text>
          )}
        </View>

        {/* 1) Lesson */}
        {typeOfContent === "lesson" && (
          <View style={styles.section}>
            {detailedLessonPlan && (
              <>
                <View style={{ marginBottom: 10 }}>
                  <Text style={styles.subsectionTitle}>Lesson Objective</Text>
                  <Text>{detailedLessonPlan.lessonObjective}</Text>
                </View>

                <View style={{ marginBottom: 10 }}>
                  <Text style={styles.subsectionTitle}>Materials Required</Text>
                  <Text>{detailedLessonPlan.materialsRequired}</Text>
                </View>

                <View style={{ marginBottom: 10, height: '1px', width: '100%', backgroundColor: '#aaa' }}></View>

                <View style={{ marginBottom: 10 }}>
                  <Text style={styles.subsectionTitle}>Lesson Introduction</Text>
                  <Text>{detailedLessonPlan.lessonIntroduction}</Text>
                </View>

                <View style={{ marginBottom: 10 }}>
                  <Text style={styles.subsectionTitle}>Main Teaching</Text>
                  <Text>{detailedLessonPlan.mainTeaching}</Text>
                </View>

                <View style={{ marginBottom: 10 }}>
                  <Text style={styles.subsectionTitle}>Guided Practice</Text>
                  <Text>{detailedLessonPlan.guidedPractice}</Text>
                </View>

                <View style={{ marginBottom: 10 }}>
                  <Text style={styles.subsectionTitle}>Independent Practice</Text>
                  <Text>{detailedLessonPlan.independentPractice}</Text>
                </View>

                <View style={{ marginBottom: 10 }}>
                  <Text style={styles.subsectionTitle}>Review</Text>
                  <Text>{detailedLessonPlan.review}</Text>
                </View>
              </>
            )}

            <View style={{ marginBottom: 10, height: '1px', width: '100%', backgroundColor: '#aaa' }}></View>

            <View style={{ marginBottom: 10 }}>
              <Text style={styles.subsectionTitle}>Anticipated Questions</Text>
              <Text>{anticipatedQuestions}</Text>
            </View>

            <View style={{ marginBottom: 10, height: '1px', width: '100%', backgroundColor: '#aaa' }}></View>

            <View style={{ marginBottom: 10 }}>
              <Text style={styles.subsectionTitle}>Activity</Text>
              <Text>{activity}</Text>
            </View>
          </View>
        )}

        {/* 2) Worksheet - wordMatching, sentenceUnscramble, readingComprehension */}
        {(typeOfContent === "worksheet" || typeOfContent === "worksheet_marksheet") && (
          <>
            {showWordMatching && wordMatchingData && wordMatchingData.length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Word Matching</Text>
                <WordMatching data={wordMatchingData} typeOfContent={typeOfContent} />
              </View>
            )}

            {sentenceUnscrambleData && sentenceUnscrambleData.length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Sentence Unscramble</Text>
                <SentenceUnscramble data={sentenceUnscrambleData} typeOfContent={typeOfContent} />
              </View>
            )}

            {showReadingComprehension &&
              readingComprehensionData &&
              readingComprehensionData.passage && (
                <View style={styles.section}>
                  <Text style={styles.sectionTitle}>Reading Comprehension</Text>
                  <ReadingComprehension data={readingComprehensionData} typeOfContent={typeOfContent} />
                </View>
              )}
          </>
        )}

        {/* 3) Homework - writingPrompt, fillInBlank */}
        {(typeOfContent === "homework" || typeOfContent === "homework_marksheet") && (
          <>
            {showFillInBlank && fillInBlankData && fillInBlankData.length > 0 && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Fill in the Blank</Text>
                <FillInBlank data={fillInBlankData} typeOfContent={typeOfContent} />
              </View>
            )}

            {showWritingPrompt && writingPrompt && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>Writing Prompt</Text>
                <Text style={styles.writingPrompt}>{writingPrompt.prompt}</Text>
                <Text style={styles.writingPromptInstructions}>{writingPrompt.instructions}</Text>

                <View style={{ marginTop: 40, marginBottom: 28, height: '1px', width: '100%', backgroundColor: '#aaa' }}></View>
                <View style={{ marginBottom: 28, height: '1px', width: '100%', backgroundColor: '#aaa' }}></View>
                <View style={{ marginBottom: 28, height: '1px', width: '100%', backgroundColor: '#aaa' }}></View>
                <View style={{ marginBottom: 28, height: '1px', width: '100%', backgroundColor: '#aaa' }}></View>
                <View style={{ marginBottom: 28, height: '1px', width: '100%', backgroundColor: '#aaa' }}></View>
                <View style={{ marginBottom: 28, height: '1px', width: '100%', backgroundColor: '#aaa' }}></View>
                <View style={{ marginBottom: 28, height: '1px', width: '100%', backgroundColor: '#aaa' }}></View>
                <View style={{ marginBottom: 28, height: '1px', width: '100%', backgroundColor: '#aaa' }}></View>
                <View style={{ marginBottom: 28, height: '1px', width: '100%', backgroundColor: '#aaa' }}></View>
                <View style={{ marginBottom: 28, height: '1px', width: '100%', backgroundColor: '#aaa' }}></View>
                <View style={{ marginBottom: 28, height: '1px', width: '100%', backgroundColor: '#aaa' }}></View>
                <View style={{ marginBottom: 28, height: '1px', width: '100%', backgroundColor: '#aaa' }}></View>
              </View>
            )}
          </>
        )}
      </Page>
    </Document>
  );
}
